import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MapPin, ArrowRight } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { getStats, getHearts, getFileUrl } from '@/lib/api';
import { cityConfig } from '@/config/cities';

// Animated counter hook
const useCountUp = (end, duration = 1200) => {
  const [value, setValue] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (end <= 0 || started.current) return;
    started.current = true;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration]);
  return value;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'hier';
  const days = Math.floor(diff / 86400);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const HERO_BG = cityConfig.heroBg;

const Landing = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_discovered: 0, total_explorers: 0, remaining: cityConfig.totalHearts });
  const [recentHearts, setRecentHearts] = useState([]);
  const animatedDiscovered = useCountUp(stats.total_discovered, 1400);
  const animatedExplorers = useCountUp(stats.total_explorers, 1400);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, heartsData] = await Promise.all([getStats(), getHearts()]);
        setStats(statsData);
        setRecentHearts(heartsData);
      } catch (e) {
        console.error('Failed to fetch data:', e);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col relative" style={{ overscrollBehavior: 'none' }} data-testid="landing-page">

      {/* Background — Toits de Paris bleu nuit + lumières chaudes */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${HERO_BG}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
          }}
        />
        {/* Overlay léger — image déjà parfaite, juste fondre le bas */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(
              to bottom,
              rgba(8, 12, 25, 0.15) 0%,
              rgba(8, 12, 25, 0.25) 30%,
              rgba(8, 12, 25, 0.5) 55%,
              rgba(8, 10, 22, 0.88) 75%,
              rgb(8, 10, 20) 90%,
              rgb(8, 10, 20) 100%
            )`,
          }}
        />
      </div>

      {/* Hero — 80% */}
      <div className="flex flex-col items-center justify-center px-6 text-center relative z-10" style={{ minHeight: '80dvh' }}>

        {/* Heart with warm glow */}
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
          className="relative mb-5">
          <div className="absolute inset-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full mx-auto pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(230,57,70,0.5) 0%, rgba(180,40,50,0.15) 50%, transparent 70%)', transform: 'scale(3)' }} />
          <Heart className="w-14 h-14 sm:w-16 sm:h-16 text-[#E63946] heart-pulse relative z-10 drop-shadow-[0_0_20px_rgba(230,57,70,0.5)]" fill="#E63946" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-4 cursor-pointer"
          onClick={() => navigate('/map')}
          style={{ textShadow: '0 2px 24px rgba(255,255,255,0.55), 0 0 1px rgba(255,255,255,0.95)' }}>
          <span className="text-black">HE</span><span className="text-[#E63946] drop-shadow-[0_0_20px_rgba(230,57,70,0.5)]">ART</span><span className="text-black">S</span>
          <span className="text-black ml-3 sm:ml-4">IN {cityConfig.name.toUpperCase()}</span>
        </motion.h1>

        {/* Signature de l'artiste */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}
          className="mb-8 max-w-[380px] mx-auto">
          <p className="text-black text-base sm:text-lg font-semibold italic mb-1.5"
            style={{ textShadow: '0 2px 20px rgba(255,255,255,0.55), 0 0 1px rgba(255,255,255,0.9)' }}>
            {cityConfig.subtitle}
          </p>
          <p className="text-black text-sm leading-relaxed whitespace-pre-line"
            style={{ textShadow: '0 1px 18px rgba(255,255,255,0.55), 0 0 1px rgba(255,255,255,0.9)' }}>
            {cityConfig.description}
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="flex flex-col items-center mb-3">
          <div className="flex items-center gap-10 sm:gap-16">
            <div className="text-center">
              <div className="text-5xl sm:text-6xl font-display font-bold text-[#E63946]"
                style={{ textShadow: '0 0 30px rgba(230,57,70,0.4), 0 2px 10px rgba(0,0,0,0.5)' }}>
                {animatedDiscovered}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2 font-medium"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
                / {cityConfig.totalHearts}
              </div>
            </div>
            <div className="w-px h-16 bg-transparent" />
            <div className="text-center">
              <div className="text-5xl sm:text-6xl font-display font-bold text-white"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>
                {animatedExplorers}
              </div>
              <div className="text-xs tracking-[0.2em] uppercase text-white/60 mt-2 font-medium"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>
                Finders
              </div>
            </div>
          </div>
          {/* Progress bar — fine, subtile */}
          <div className="w-48 h-[2px] mt-4 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: '#E63946' }}
              initial={{ width: 0 }}
              animate={{ width: `${Math.max((stats.total_discovered / cityConfig.totalHearts) * 100, 0.5)}%` }}
              transition={{ delay: 0.8, duration: 1.2, ease: 'easeOut' }}
            />
          </div>
          {/* Verified / Declared counts */}
          {stats.total_discovered > 0 && (
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <Heart className="w-3 h-3 text-[#E63946]" fill="#E63946" />
                <span className="text-[#E63946] text-xs font-medium">{stats.total_verified || 0} vérifiés</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Heart className="w-3 h-3 text-white" fill="white" />
                <span className="text-white/60 text-xs font-medium">{stats.total_declared || 0} déclarés</span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Urgency */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
          className="text-xs tracking-wide mb-8 font-bold uppercase"
          style={{ color: '#fff', textShadow: '0 0 20px rgba(230,57,70,0.6), 0 1px 4px rgba(0,0,0,0.8)' }}>
          {stats.remaining} cœurs encore cachés dans {cityConfig.name}
        </motion.p>

        {/* CTA — J'AI TROUVÉ UN CŒUR ! */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="flex flex-col items-center gap-3 w-full max-w-sm px-2">
          <button
            onClick={() => navigate('/find')}
            className="group w-full bg-[#E63946] text-white font-bold tracking-wider uppercase py-4 flex items-center justify-center gap-3 rounded-sm hover:-translate-y-0.5 active:scale-[0.98]"
            style={{ boxShadow: '0 6px 35px rgba(230, 57, 70, 0.55), 0 2px 15px rgba(0,0,0,0.3)', touchAction: 'manipulation', transition: 'transform 0.2s ease' }}
            data-testid="start-button">
            <Heart className="w-5 h-5" fill="white" />
            J'ai trouvé un cœur !
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <span className="text-white/50 text-[10px] tracking-[0.3em] uppercase font-medium"
            style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>
            prenez · ressentez · partagez
          </span>
        </motion.div>
      </div>

      {/* Recent discoveries */}
      {recentHearts.length > 0 && (
        <div className="relative z-10 px-4 pb-4">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-semibold"
              style={{ textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>
              Toutes les découvertes
            </h2>
            <button onClick={() => navigate('/gallery')}
              className="text-[10px] text-[#ff6b7a] hover:text-white transition-colors uppercase tracking-wider flex items-center gap-1"
              data-testid="view-all-button">
              Tout voir <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="space-y-1" data-testid="recent-discoveries">
            {recentHearts.map((heart, i) => (
              <motion.button
                key={heart.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 + i * 0.03 }}
                onClick={() => navigate(`/heart/${heart.heart_number}`)}
                className="w-full flex items-center gap-2.5 px-3 py-2 bg-white/[0.06] backdrop-blur-sm border-l-2 border-[#E63946]/60 hover:border-[#E63946] hover:bg-white/[0.1] transition-all text-left rounded-r-sm"
                data-testid={`discovery-row-${heart.heart_number}`}
              >
                {heart.close_photo_path ? (
                  <div className="w-8 h-8 bg-[#27272A] overflow-hidden flex-shrink-0 rounded-sm">
                    <img src={getFileUrl(heart.close_photo_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-white/[0.06] flex items-center justify-center flex-shrink-0 rounded-sm">
                    <Heart className={`w-3 h-3 ${heart.verification_status === 'verified' ? 'text-[#E63946]' : 'text-white'}`} fill={heart.verification_status === 'verified' ? '#E63946' : 'white'} />
                  </div>
                )}
                <span className={`font-display font-bold text-sm w-12 flex-shrink-0 ${heart.verification_status === 'verified' ? 'text-[#ff6b7a]' : 'text-white'}`}>#{heart.heart_number}</span>
                <div className="flex-1 min-w-0 text-[11px] text-white/75 truncate flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5 flex-shrink-0 text-white/45" />{heart.location_name || cityConfig.name}
                </div>
                <span className="text-[10px] text-white/40 flex-shrink-0 whitespace-nowrap">{timeAgo(heart.discovered_at)}</span>
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
